import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import {
  FileText,
  FolderOpen,
  Image,
  Users,
  Download,
  Plus,
  TrendingUp,
  Eye,
} from 'lucide-react';
import { statsAPI, postsAPI, seedAPI } from '../../lib/api';
import { formatDateShort } from '../../lib/utils';
import { toast } from 'sonner';

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [recentPosts, setRecentPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [statsRes, postsRes] = await Promise.all([
        statsAPI.get(),
        postsAPI.getAll({ limit: 5 }),
      ]);
      setStats(statsRes.data);
      setRecentPosts(postsRes.data.posts);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSeed = async () => {
    setSeeding(true);
    try {
      await seedAPI.seed();
      toast.success('Dados iniciais criados com sucesso!');
      loadData();
    } catch (error) {
      if (error.response?.data?.message?.includes('já possui')) {
        toast.info('Banco de dados já possui dados iniciais');
      } else {
        toast.error('Erro ao criar dados iniciais');
      }
    } finally {
      setSeeding(false);
    }
  };

  const statCards = [
    {
      title: 'Total de Artigos',
      value: stats?.posts?.total || 0,
      subtext: `${stats?.posts?.published || 0} publicados`,
      icon: FileText,
      color: 'bg-blue-500',
      link: '/admin/posts',
    },
    {
      title: 'Categorias',
      value: stats?.categories || 0,
      subtext: 'categorias ativas',
      icon: FolderOpen,
      color: 'bg-green-500',
      link: '/admin/categories',
    },
    {
      title: 'Arquivos de Mídia',
      value: stats?.media || 0,
      subtext: 'arquivos enviados',
      icon: Image,
      color: 'bg-purple-500',
      link: '/admin/media',
    },
    {
      title: 'Leads Capturados',
      value: stats?.leads || 0,
      subtext: 'contatos recebidos',
      icon: Users,
      color: 'bg-amber-500',
      link: '/admin/leads',
    },
    {
      title: 'Downloads de Materiais',
      value: stats?.downloads || 0,
      subtext: 'PDFs baixados em /cipe',
      icon: Download,
      color: 'bg-teal-600',
      link: '/cipe',
    },
  ];

  return (
    <div data-testid="admin-dashboard">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900">Dashboard</h1>
          <p className="text-slate-600">Visão geral do seu site e blog</p>
        </div>
        <div className="flex gap-3">
          <Button
            onClick={handleSeed}
            variant="outline"
            disabled={seeding}
            className="btn-secondary"
            data-testid="seed-button"
          >
            {seeding ? 'Criando...' : 'Criar dados iniciais'}
          </Button>
          <Button asChild className="btn-primary">
            <Link to="/admin/posts/new">
              <Plus className="w-4 h-4 mr-2" />
              Novo Artigo
            </Link>
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
        {statCards.map((card) => {
          const Icon = card.icon;
          return (
            <Link key={card.title} to={card.link}>
              <Card className="hover:shadow-md transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-sm text-slate-500 mb-1">{card.title}</p>
                      <p className="text-3xl font-bold text-slate-900">
                        {loading ? '-' : card.value}
                      </p>
                      <p className="text-xs text-slate-400 mt-1">{card.subtext}</p>
                    </div>
                    <div className={`w-12 h-12 rounded-xl ${card.color} flex items-center justify-center`}>
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </Link>
          );
        })}
      </div>

      {/* Recent Posts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>Artigos Recentes</span>
              <Button asChild variant="ghost" size="sm">
                <Link to="/admin/posts">Ver todos</Link>
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-4">
                {Array(3).fill(0).map((_, i) => (
                  <div key={i} className="skeleton h-16 w-full rounded-lg" />
                ))}
              </div>
            ) : recentPosts.length > 0 ? (
              <div className="space-y-4">
                {recentPosts.map((post) => (
                  <Link
                    key={post.id}
                    to={`/admin/posts/${post.id}`}
                    className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-slate-900 truncate">{post.title}</p>
                      <p className="text-sm text-slate-500">
                        {formatDateShort(post.created_at)}
                      </p>
                    </div>
                    <div className="flex items-center gap-4 text-sm">
                      <span className={post.status === 'published' ? 'status-published' : 'status-draft'}>
                        {post.status === 'published' ? 'Publicado' : 'Rascunho'}
                      </span>
                      <span className="flex items-center gap-1 text-slate-400">
                        <Eye className="w-4 h-4" />
                        {post.views}
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-slate-500 mb-4">Nenhum artigo ainda.</p>
                <Button asChild className="btn-primary" size="sm">
                  <Link to="/admin/posts/new">
                    <Plus className="w-4 h-4 mr-2" />
                    Criar primeiro artigo
                  </Link>
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <Card>
          <CardHeader>
            <CardTitle>Ações Rápidas</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <Button asChild className="w-full justify-start" variant="outline">
                <Link to="/admin/posts/new">
                  <Plus className="w-4 h-4 mr-3" />
                  Criar novo artigo
                </Link>
              </Button>
              <Button asChild className="w-full justify-start" variant="outline">
                <Link to="/admin/categories">
                  <FolderOpen className="w-4 h-4 mr-3" />
                  Gerenciar categorias
                </Link>
              </Button>
              <Button asChild className="w-full justify-start" variant="outline">
                <Link to="/admin/media">
                  <Image className="w-4 h-4 mr-3" />
                  Biblioteca de mídia
                </Link>
              </Button>
              <Button asChild className="w-full justify-start" variant="outline">
                <Link to="/admin/leads">
                  <Users className="w-4 h-4 mr-3" />
                  Ver leads capturados
                </Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminDashboard;
